import {
  BubbleMenu,
  useCurrentEditor,
  useEditorState,
  type BubbleMenuProps,
  type Editor,
} from "@tiptap/react";
import { CellSelection } from "@tiptap/pm/tables";
import { NodeSelection } from "@tiptap/pm/state";
import {
  BetweenHorizontalEnd,
  BetweenHorizontalStart,
  BetweenVerticalEnd,
  BetweenVerticalStart,
  Bold,
  Check,
  ChevronDown,
  Code,
  ExternalLink,
  Italic,
  Link as LinkIcon,
  Pencil,
  Strikethrough,
  Unlink,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { TEXT_BLOCK_TYPES } from "./block-types";
import { listenEscapeFirst } from "./escape-first";

const BUBBLE_CLASS = "flex items-center gap-0.5 rounded-md border bg-popover p-1 shadow-md";

const BubbleButton = ({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-pressed={active}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={cn(
      "flex h-7 min-w-7 items-center justify-center gap-1 rounded px-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
      active && "bg-muted text-foreground",
    )}
  >
    {children}
  </button>
);

const Separator = () => <div className="mx-0.5 h-5 w-px bg-border" />;

const LinkInput = ({ editor, onDone }: { editor: Editor; onDone: () => void }) => {
  const [url, setUrl] = useState<string>(editor.getAttributes("link").href ?? "");

  const cancel = () => {
    onDone();
    editor.commands.focus();
  };
  useEffect(() => listenEscapeFirst(cancel));

  const apply = () => {
    const chain = editor.chain().focus().extendMarkRange("link");
    (url.trim() ? chain.setLink({ href: url.trim() }) : chain.unsetLink()).run();
    onDone();
  };

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        apply();
      }}
    >
      <input
        autoFocus
        type="text"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste or type a link"
        className="h-7 w-64 rounded border-0 bg-transparent px-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground"
      />
      <BubbleButton title="Apply link" onClick={apply}>
        <Check size={14} />
      </BubbleButton>
    </form>
  );
};

type TippyInstance = Parameters<
  NonNullable<NonNullable<BubbleMenuProps["tippyOptions"]>["onCreate"]>
>[0];

/**
 * Tippy only flips when it repositions, so a bubble that grows in place (e.g. the
 * turn-into list) must be repositioned or it overflows the dialog it sits in.
 */
const useRepositionOn = (dep: unknown) => {
  const tippy = useRef<TippyInstance | null>(null);
  useEffect(() => {
    const frame = requestAnimationFrame(() => tippy.current?.popperInstance?.update());
    return () => cancelAnimationFrame(frame);
  }, [dep]);
  return (instance: TippyInstance) => {
    tippy.current = instance;
  };
};

/** `mode` falls back to `initial` whenever the selection moves. */
const useModeResetOnSelection = <M extends string>(editor: Editor, initial: M) => {
  const [mode, setMode] = useState<M>(initial);
  useEffect(() => {
    const reset = () => setMode(initial);
    editor.on("selectionUpdate", reset);
    return () => {
      editor.off("selectionUpdate", reset);
    };
  }, [editor, initial]);
  return [mode, setMode] as const;
};

/** `linkRequest` increments on ⌘K. */
export const FormatBubble = ({ linkRequest }: { linkRequest: number }) => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;
  return <FormatBubbleInner editor={editor} linkRequest={linkRequest} />;
};

const FormatBubbleInner = ({ editor, linkRequest }: { editor: Editor; linkRequest: number }) => {
  const [mode, setMode] = useModeResetOnSelection<"format" | "link" | "turnInto">(editor, "format");
  const onCreate = useRepositionOn(mode);

  useEffect(() => {
    if (linkRequest && !editor.state.selection.empty) setMode("link");
  }, [linkRequest, editor, setMode]);

  const marks = useEditorState({
    editor,
    selector: ({ editor }) => ({
      blockId: (TEXT_BLOCK_TYPES.find((t) => t.isActive?.(editor)) ?? TEXT_BLOCK_TYPES[0]).id,
      bold: editor.isActive("bold"),
      italic: editor.isActive("italic"),
      strike: editor.isActive("strike"),
      code: editor.isActive("code"),
      link: editor.isActive("link"),
    }),
  });
  const current = TEXT_BLOCK_TYPES.find((t) => t.id === marks.blockId) ?? TEXT_BLOCK_TYPES[0];

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="formatBubble"
      shouldShow={({ editor, state, from, to }) =>
        editor.isEditable &&
        from !== to &&
        !(state.selection instanceof NodeSelection) &&
        !(state.selection instanceof CellSelection) &&
        !editor.isActive("codeBlock")
      }
      tippyOptions={{ placement: "top-start", appendTo: "parent", maxWidth: "none", onCreate }}
    >
      <div className={BUBBLE_CLASS}>
        {mode === "link" && <LinkInput editor={editor} onDone={() => setMode("format")} />}
        {mode === "turnInto" && (
          <div className="flex min-w-44 flex-col">
            {TEXT_BLOCK_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  type.apply(editor.chain().focus()).run();
                  setMode("format");
                }}
                className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted"
              >
                <type.icon size={16} className="text-muted-foreground" />
                <span className="flex-1">{type.label}</span>
                {type.id === current.id && <Check size={14} className="text-muted-foreground" />}
              </button>
            ))}
          </div>
        )}
        {mode === "format" && (
          <>
            <BubbleButton title="Turn into" onClick={() => setMode("turnInto")}>
              <span className="px-0.5 text-foreground">{current.label}</span>
              <ChevronDown size={12} />
            </BubbleButton>
            <Separator />
            <BubbleButton
              title="Bold (⌘B)"
              active={marks.bold}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold size={14} />
            </BubbleButton>
            <BubbleButton
              title="Italic (⌘I)"
              active={marks.italic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic size={14} />
            </BubbleButton>
            <BubbleButton
              title="Strikethrough (⌘⇧S)"
              active={marks.strike}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <Strikethrough size={14} />
            </BubbleButton>
            <BubbleButton
              title="Inline code (⌘E)"
              active={marks.code}
              onClick={() => editor.chain().focus().toggleCode().run()}
            >
              <Code size={14} />
            </BubbleButton>
            <BubbleButton title="Link (⌘K)" active={marks.link} onClick={() => setMode("link")}>
              <LinkIcon size={14} />
            </BubbleButton>
          </>
        )}
      </div>
    </BubbleMenu>
  );
};

/** Shown while the caret sits inside a link. */
export const LinkBubble = ({ linkRequest }: { linkRequest: number }) => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;
  return <LinkBubbleInner editor={editor} linkRequest={linkRequest} />;
};

const LinkBubbleInner = ({ editor, linkRequest }: { editor: Editor; linkRequest: number }) => {
  const [mode, setMode] = useModeResetOnSelection<"view" | "edit">(editor, "view");
  const onCreate = useRepositionOn(mode);
  const href = useEditorState({
    editor,
    selector: ({ editor }): string => editor.getAttributes("link").href ?? "",
  });

  useEffect(() => {
    if (linkRequest && editor.state.selection.empty && editor.isActive("link")) setMode("edit");
  }, [linkRequest, editor, setMode]);

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="linkBubble"
      shouldShow={({ editor, from, to }) =>
        editor.isEditable && from === to && editor.isActive("link")
      }
      tippyOptions={{ placement: "bottom-start", appendTo: "parent", maxWidth: "none", onCreate }}
    >
      <div className={BUBBLE_CLASS}>
        {mode === "edit" ? (
          <LinkInput editor={editor} onDone={() => setMode("view")} />
        ) : (
          <>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-7 max-w-72 items-center gap-1.5 truncate rounded px-1.5 text-sm text-foreground hover:bg-muted"
            >
              <ExternalLink size={14} className="shrink-0 text-muted-foreground" />
              <span className="truncate">{href}</span>
            </a>
            <Separator />
            <BubbleButton title="Edit link (⌘K)" onClick={() => setMode("edit")}>
              <Pencil size={14} />
            </BubbleButton>
            <BubbleButton
              title="Remove link"
              onClick={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()}
            >
              <Unlink size={14} />
            </BubbleButton>
          </>
        )}
      </div>
    </BubbleMenu>
  );
};

const tableRect = (editor: Editor) => {
  const { node } = editor.view.domAtPos(editor.state.selection.from);
  const element = node instanceof Element ? node : node.parentElement;
  return (element?.closest("table") ?? editor.view.dom).getBoundingClientRect();
};

/** Shown while the caret or a cell selection is inside a table. */
export const TableBubble = () => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  const run = (fn: (chain: ReturnType<Editor["chain"]>) => ReturnType<Editor["chain"]>) => () =>
    fn(editor.chain().focus()).run();

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="tableBubble"
      shouldShow={({ editor, state }) =>
        editor.isEditable &&
        editor.isActive("table") &&
        (state.selection.empty || state.selection instanceof CellSelection)
      }
      tippyOptions={{
        placement: "bottom-start",
        appendTo: "parent",
        maxWidth: "none",
        getReferenceClientRect: () => tableRect(editor),
      }}
    >
      <div className={BUBBLE_CLASS}>
        <BubbleButton title="Insert row above" onClick={run((c) => c.addRowBefore())}>
          <BetweenHorizontalStart size={14} />
        </BubbleButton>
        <BubbleButton title="Insert row below" onClick={run((c) => c.addRowAfter())}>
          <BetweenHorizontalEnd size={14} />
        </BubbleButton>
        <BubbleButton title="Insert column left" onClick={run((c) => c.addColumnBefore())}>
          <BetweenVerticalStart size={14} />
        </BubbleButton>
        <BubbleButton title="Insert column right" onClick={run((c) => c.addColumnAfter())}>
          <BetweenVerticalEnd size={14} />
        </BubbleButton>
        <Separator />
        <span className="px-1.5 text-xs text-muted-foreground">Delete</span>
        <BubbleButton title="Delete row" onClick={run((c) => c.deleteRow())}>
          Row
        </BubbleButton>
        <BubbleButton title="Delete column" onClick={run((c) => c.deleteColumn())}>
          Column
        </BubbleButton>
        <BubbleButton title="Delete table" onClick={run((c) => c.deleteTable())}>
          Table
        </BubbleButton>
      </div>
    </BubbleMenu>
  );
};
